/**
 * 目录树节点组件 - 递归渲染单个 OutlineItem
 */
import React from 'react';
import { OutlineItem } from '../types';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';

interface OutlineTreeNodeProps {
  item: OutlineItem;
  level: number;
  expandedItems: Set<string>;
  editingItem: string | null;
  editTitle: string;
  editDescription: string;
  editWordCountRange: string;
  descMaximized: boolean;
  onToggleExpand: (itemId: string) => void;
  onStartEdit: (item: OutlineItem) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onSetEditTitle: (v: string) => void;
  onSetEditDescription: (v: string) => void;
  onSetEditWordCountRange: (v: string) => void;
  onToggleDescMaximized: (v: boolean) => void;
  onDeleteItem: (itemId: string) => void;
  onAddChildItem: (parentId: string) => void;
  parseWordCountUpper: (range: string | undefined) => number;
  readOnly?: boolean;
}

const OutlineTreeNode: React.FC<OutlineTreeNodeProps> = ({
  item,
  level,
  expandedItems,
  editingItem,
  editTitle,
  editDescription,
  editWordCountRange,
  descMaximized,
  onToggleExpand,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onSetEditTitle,
  onSetEditDescription,
  onSetEditWordCountRange,
  onToggleDescMaximized,
  onDeleteItem,
  onAddChildItem,
  parseWordCountUpper,
  readOnly = false,
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.has(item.id);
  const isLeaf = !hasChildren;
  const isEditing = editingItem === item.id;

  return (
    <div className={`${level > 0 ? 'ml-6' : ''}`}>
      <div className="group flex items-start space-x-2 py-2 hover:bg-gray-50 rounded px-2">
        {hasChildren ? (
          <button
            onClick={() => onToggleExpand(item.id)}
            className="mt-1 p-0.5 rounded hover:bg-gray-200"
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
            )}
          </button>
        ) : (
          <DocumentTextIcon className="mt-1 h-4 w-4 text-gray-400" />
        )}

        <div className="flex-1 min-w-0">
          {isEditing ? (
            /* 编辑模式 */
            <div className="space-y-2">
              <input
                type="text"
                value={editTitle}
                onChange={(e) => onSetEditTitle(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="目录标题"
              />
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-gray-500">目录描述</label>
                  <button
                    type="button"
                    onClick={() => onToggleDescMaximized(!descMaximized)}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    title={descMaximized ? '还原' : '最大化'}
                  >
                    {descMaximized ? (
                      <ArrowsPointingInIcon className="h-3.5 w-3.5" />
                    ) : (
                      <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
                    )}
                    <span className="ml-1">{descMaximized ? '还原' : '最大化'}</span>
                  </button>
                </div>
                <textarea
                  value={editDescription}
                  onChange={(e) => onSetEditDescription(e.target.value)}
                  rows={descMaximized ? 16 : 4}
                  className={`w-full px-2 py-1.5 border border-gray-300 rounded text-sm transition-all ${
                    descMaximized ? 'min-h-[320px] resize-y' : 'resize-y'
                  }`}
                  placeholder="请输入该章节的详细描述、核心要点和关键内容..."
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">字数范围</label>
                <input
                  type="text"
                  value={editWordCountRange}
                  onChange={(e) => onSetEditWordCountRange(e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="例如: 800-1500"
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={onSaveEdit}
                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                >
                  保存
                </button>
                <button
                  onClick={onCancelEdit}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  取消
                </button>
              </div>
            </div>
          ) : (
            /* 正常显示模式 */
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span
                    className={`text-sm font-medium ${
                      level === 0
                        ? 'text-blue-600'
                        : level === 1
                          ? 'text-green-600'
                          : 'text-gray-700'
                    }`}
                  >
                    {item.id} {item.title}
                  </span>
                  {isLeaf && item.word_count_range && (
                    <span className="text-xs text-purple-500 font-medium">
                      （字数：{parseWordCountUpper(item.word_count_range)}）
                    </span>
                  )}
                  {item.content && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      已生成内容
                    </span>
                  )}
                </div>

                {/* 操作按钮组 */}
                {!readOnly && (
                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onStartEdit(item)}
                    className="p-1 rounded hover:bg-blue-100 text-blue-600"
                    title="编辑"
                  >
                    <PencilIcon className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => onAddChildItem(item.id)}
                    className="p-1 rounded hover:bg-green-100 text-green-600"
                    title="添加子目录"
                  >
                    <PlusIcon className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => onDeleteItem(item.id)}
                    className="p-1 rounded hover:bg-red-100 text-red-600"
                    title="删除"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">{item.description}</p>

              {/* 显示生成的内容（如果有） */}
              {item.content && isLeaf && (
                <div className="mt-2 p-3 bg-gray-50 rounded-md border-l-4 border-blue-200">
                  <div className="text-xs text-gray-600 whitespace-pre-wrap">
                    {item.content}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {item.children!.map((child) => (
            <OutlineTreeNode
              key={child.id}
              item={child}
              level={level + 1}
              expandedItems={expandedItems}
              editingItem={editingItem}
              editTitle={editTitle}
              editDescription={editDescription}
              editWordCountRange={editWordCountRange}
              descMaximized={descMaximized}
              onToggleExpand={onToggleExpand}
              onStartEdit={onStartEdit}
              onSaveEdit={onSaveEdit}
              onCancelEdit={onCancelEdit}
              onSetEditTitle={onSetEditTitle}
              onSetEditDescription={onSetEditDescription}
              onSetEditWordCountRange={onSetEditWordCountRange}
              onToggleDescMaximized={onToggleDescMaximized}
              onDeleteItem={onDeleteItem}
              onAddChildItem={onAddChildItem}
              parseWordCountUpper={parseWordCountUpper}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default OutlineTreeNode;
